import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Camera, Loader2, ScanSearch, FileText, Save, X, Sparkles, Upload,
  RefreshCw, Cloud, Check, AlertCircle,
} from "lucide-react";
import CaptureGuideDialog from "./CaptureGuideDialog";
import ManualCropDialog from "./ManualCropDialog";

// V1.36 · Diálogo "Nuevo gasto".
// Flujo: Foto → Filtro escaneado → OCR (Gemini Flash) → Formulario editable → Guardar.

const SCAN_FILTERS = [
  { key: "office",    label: "Oficina",   hint: "Fondo blanco, letras nítidas (recomendado)" },
  { key: "original",  label: "Original",  hint: "Sin retoque" },
  { key: "fotocopia", label: "Fotocopia", hint: "B/N contrastado" },
  { key: "escaner",   label: "Escáner",   hint: "Alta legibilidad B/N" },
];

async function fileToDataUrl(file) {
  return await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("No se pudo leer el archivo"));
    r.readAsDataURL(file);
  });
}

// V1.40.4 · Motor de captura AISLADO — copia exacta del motor que ya
// funciona en DNI / Psicotécnico / Cuestionario (AltaDigitalPage.jsx).
// **NO reutilizar por import**: la regla es "copiar y aislar" para
// eliminar riesgo de regresión en los módulos existentes.
// Redimensiona a lado mayor ≤1600 px y comprime JPEG q=0.85. Sin
// `createImageBitmap`, sin triple fallback, sin timeouts complejos.
async function fileToJpegDataUrl(file, maxSide = 1600, quality = 0.85) {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Imagen no válida"));
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function ExpenseNewDialog({ open, sectionId, sectionLabel, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1=capture, 2=review, 3=saving
  const [imageDataUrl, setImageDataUrl] = useState("");        // ORIGINAL raw
  const [scannedDataUrl, setScannedDataUrl] = useState("");    // POST scan pipeline
  const [scanMeta, setScanMeta] = useState(null);              // V1.43 · transformaciones aplicadas
  const [viewMode, setViewMode] = useState("procesada");       // V1.43 · "original" | "procesada"
  const [scanQuality, setScanQuality] = useState(null);        // {ok, problems, metrics}
  const [scanFilter, setScanFilter] = useState("original");
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrStep, setOcrStep] = useState(null); // "prep" | "llm" | "supplier" | null
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrRoute, setOcrRoute] = useState(null); // "image" | "pdf_native" | null
  const [saving, setSaving] = useState(false);
  // V1.40.4 · Previsualización simple tras hacer foto (paso 1.5)
  const [photoPreview, setPhotoPreview] = useState("");
  // V1.77 · CROP manual sobre la foto antes del OCR (mismo motor que Alumnos)
  const [cropOpen, setCropOpen] = useState(false);
  const [cropped, setCropped] = useState(false);

  // Form state (rellenable manualmente si OCR falla)
  const [form, setForm] = useState({
    supplier_id: "",
    supplier_name: "",
    supplier_cif: "",
    invoice_number: "",
    invoice_date: "",
    base_amount: 0,
    vat_amount: 0,
    vat_percent: 21,
    total_amount: 0,
    main_account: "",
    expense_kind: "",
    vehicle_id: "",
    notes: "",
    ocr_confidence: 0,
    ocr_engine: "",
  });

  // Estructura contable + proveedores
  const [tree, setTree] = useState({});
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (!open) return;
    setStep(1);
    setImageDataUrl("");
    setScannedDataUrl("");
    setScanMeta(null);
    setViewMode("procesada");
    setScanQuality(null);
    setScanFilter("original");
    setPhotoPreview("");
    setOcrStep(null);
    setForm({
      supplier_id: "", supplier_name: "", supplier_cif: "",
      invoice_number: "", invoice_date: "",
      base_amount: 0, vat_amount: 0, vat_percent: 21, total_amount: 0,
      main_account: "", expense_kind: "", vehicle_id: "", notes: "",
      ocr_confidence: 0, ocr_engine: "",
    });
    (async () => {
      try {
        const [{ data: accData }, { data: supData }] = await Promise.all([
          api.get("/accounting/accounts", { params: { active_only: true } }),
          api.get("/accounting/suppliers", { params: { active_only: true } }),
        ]);
        setTree(accData?.tree || {});
        setSuppliers(supData?.items || []);
      } catch (err) {
        console.warn("carga catálogos", err);
      }
    })();
  }, [open]);

  const photoInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const openPhotoPicker = () => photoInputRef.current?.click();
  // V1.41 · Guía de encuadre visual previa a la cámara nativa (factura).
  const [guideOpen, setGuideOpen] = useState(false);
  const openPdfPicker = () => pdfInputRef.current?.click();

  // Drag & Drop SOLO para PDF (según directriz V1.40.4)
  const [pdfDragOver, setPdfDragOver] = useState(false);
  const onPdfDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setPdfDragOver(true); };
  const onPdfDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setPdfDragOver(false); };
  const onPdfDrop = async (e) => {
    e.preventDefault(); e.stopPropagation(); setPdfDragOver(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) await handlePickPdf(f);
  };

  // Handlers de los `<input type="file">`
  const onPhotoInputChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) await handlePickPhoto(f);
  };
  const onPdfInputChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) await handlePickPdf(f);
  };

  // V1.40.4 · Dos flujos ESTANCOS, aislados uno del otro.
  //   handlePickPhoto  → foto (cámara/galería) → preview → OCR
  //   handlePickPdf    → PDF (drag&drop o picker) → OCR directo
  //
  // Ambos usan el motor SIMPLE `fileToJpegDataUrl` (para foto) o
  // `fileToDataUrl` (para PDF) — mismos que en DNI/Psicotécnico/
  // Cuestionario. Cero magia, cero lógica experimental.
  const handlePickPhoto = useCallback(async (f) => {
    if (!f) return;
    if (!sectionId) {
      toast.error("Falta seleccionar sección antes de subir el gasto");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("Foto demasiado grande (máx 15 MB)");
      return;
    }
    if (!/^image\//i.test(f.type)) {
      toast.error("Formato no soportado. Sube una imagen.");
      return;
    }
    try {
      const dataUrl = await fileToJpegDataUrl(f, 1600, 0.85);
      setPhotoPreview(dataUrl);
      // Nos quedamos en step 1 mostrando preview + [Repetir] / [Continuar]
    } catch (err) {
      toast.error(err?.message || "No se pudo preparar la foto");
       
      console.error("[expense] handlePickPhoto error", err);
    }
  }, [sectionId]);

  const handlePickPdf = useCallback(async (f) => {
    if (!f) return;
    if (!sectionId) {
      toast.error("Falta seleccionar sección antes de subir el gasto");
      return;
    }
    if (f.size > 15 * 1024 * 1024) {
      toast.error("PDF demasiado grande (máx 15 MB)");
      return;
    }
    if (f.type !== "application/pdf") {
      toast.error("Formato no soportado. Sube un PDF.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(f);
      setImageDataUrl(dataUrl);
      setStep(2);
      await runOcr(dataUrl, "original");
    } catch (err) {
      toast.error(err?.message || "No se pudo leer el PDF");
       
      console.error("[expense] handlePickPdf error", err);
    }
  }, [sectionId]);

  // V1.42 · Nuevo flujo simplificado para la CAPTURA POR CÁMARA de facturas:
  // la foto se envía directamente al OCR sin pre-procesado (el editor de
  // matriz/franquicia hará la limpieza si es necesario). El módulo de subida
  // por PDF conserva su flujo intacto — allí no aplica.
  const confirmPhotoAndOcr = useCallback(async () => {
    if (!photoPreview) return;
    setImageDataUrl(photoPreview);
    setStep(2);
     
    await runOcr(photoPreview, "original", { skipScan: true });
     
  }, [photoPreview]);

  const retakePhoto = useCallback(() => {
    setPhotoPreview("");
    setCropped(false);
  }, []);

  const runOcr = useCallback(async (imgOverride, filterOverride, opts) => {
    const img = imgOverride || imageDataUrl;
    const skipScan = !!opts?.skipScan;
    // V1.40.4 · Filtro por defecto = "original". Ningún filtro se aplica
    // automáticamente al escaneo. El usuario decide desde el selector.
    const flt = filterOverride || scanFilter || "original";
    if (!img) return;
    if (skipScan) setScannedDataUrl("");
    setOcrBusy(true);
    setOcrStep("prep");
    setOcrProgress(0);
    setOcrRoute(null);
    // V1.39.3 · Flujo asíncrono con polling — evita el timeout de 30s
    // de Cloudflare en producción.  1) crea el job → 2) polling cada
    // 1.5 s hasta done|error (máx 90 s).
    let pollTimer = null;
    try {
      const { data: created } = await api.post(
        "/accounting/expenses/ocr-jobs",
        { image_data_url: img, scan_filter: flt, skip_scan: skipScan },
      );
      const jobId = created?.job_id;
      if (!jobId) throw new Error("OCR: no se recibió job_id del servidor");

      const started = Date.now();
      const MAX_MS = 90_000;
      const POLL_MS = 700;

      // Bucle de polling
      // Cada iteración lee estado y actualiza progreso local
       
      while (true) {
        if (Date.now() - started > MAX_MS) {
          throw new Error("OCR: timeout (>90 s). Prueba con una imagen más pequeña o vuelve a intentarlo.");
        }
        await new Promise((r) => { pollTimer = setTimeout(r, POLL_MS); });
        const { data: job } = await api.get(`/accounting/expenses/ocr-jobs/${jobId}`);
        setOcrProgress(job?.progress || 0);
        if (job?.route) setOcrRoute(job.route);
        if (job?.stage) setOcrStep(job.stage === "queued" ? "prep" : job.stage);
        if (job?.status === "done") {
          const data = job.result || {};
          if (data.scanned_image_data_url) {
            setScannedDataUrl(data.scanned_image_data_url);
            setViewMode("procesada");
          } else {
            setScannedDataUrl("");
            setViewMode("original");
          }
          if (data.scan_meta) setScanMeta(data.scan_meta);
          if (data.scan_meta?.kept_original) {
            toast.info("📷 La imagen original ya era legible — el OCR se ejecutó sin ningún retoque", { duration: 5000 });
          }
          if (data.scan_meta?.quality) setScanQuality(data.scan_meta.quality);
          setForm((f) => ({
            ...f,
            supplier_id: data.supplier_id || "",
            supplier_name: data.proveedor || f.supplier_name,
            supplier_cif: data.cif || f.supplier_cif,
            invoice_number: data.numero_factura || f.invoice_number,
            invoice_date: data.fecha_factura || f.invoice_date,
            base_amount: data.base_imponible ?? f.base_amount,
            vat_amount: data.iva ?? f.vat_amount,
            vat_percent: data.iva_porcentaje || f.vat_percent,
            total_amount: data.total || f.total_amount,
            main_account: data.suggested_main_account || f.main_account,
            expense_kind: data.suggested_expense_kind || f.expense_kind,
            ocr_confidence: data.confianza || 0,
            ocr_engine: data._engine || "",
          }));
          if (data.supplier_matched) {
            toast.success(`✅ Proveedor reconocido: ${data.proveedor} (autocompletado)`);
          } else if (data.proveedor) {
            toast.success(`🔍 OCR: ${data.proveedor} — ${(data.total || 0).toFixed(2)}€`);
          }
          if (data.scan_meta?.quality && !data.scan_meta.quality.ok) {
            const probs = data.scan_meta.quality.problems || [];
            const humanize = { blur: "borroso", dark: "oscuro", overexposed: "sobreexpuesto",
                                shadowed: "con sombras", edge_cut: "cortado en bordes" };
            toast.warning(
              `⚠️ Calidad: ${probs.map((p) => humanize[p] || p).join(", ")}. Revise la vista previa antes de guardar.`,
              { duration: 6000 },
            );
          }
          break;
        }
        if (job?.status === "error") {
          throw new Error(job?.error || "OCR falló en el servidor");
        }
        // status pending|running → seguir polling
      }
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message || "OCR falló";
      toast.error(m);
    } finally {
      if (pollTimer) clearTimeout(pollTimer);
      setOcrBusy(false);
      setOcrStep(null);
      setOcrProgress(0);
      setOcrRoute(null);
    }
  }, [imageDataUrl, scanFilter]);

  const kindOptions = useMemo(() => {
    const arr = tree[form.main_account] || [];
    return arr.filter((a) => a.active).map((a) => a.kind);
  }, [tree, form.main_account]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSupplierPick = (id) => {
    if (!id) {
      setField("supplier_id", "");
      return;
    }
    const s = suppliers.find((x) => x.id === id);
    if (!s) return;
    setForm((f) => ({
      ...f,
      supplier_id: s.id,
      supplier_name: s.name,
      supplier_cif: s.cif || f.supplier_cif,
      main_account: s.main_account || f.main_account,
      expense_kind: s.expense_kind || f.expense_kind,
    }));
  };

  const canSave = (
    form.supplier_name?.trim() &&
    form.main_account && form.expense_kind &&
    form.total_amount >= 0 && imageDataUrl
  );

  const save = async () => {
    if (!canSave) {
      toast.error("Faltan campos: proveedor, cuenta o tipo de gasto");
      return;
    }
    setSaving(true);
    try {
      // V1.43 · SIEMPRE se conserva y adjunta el archivo ORIGINAL de la
      // cámara. La versión procesada sólo se usó para el OCR — el gasto
      // guarda la imagen íntegra sin transformaciones.
      const { data } = await api.post("/accounting/expenses", {
        supplier_id: form.supplier_id,
        supplier_name: form.supplier_name,
        supplier_cif: form.supplier_cif,
        invoice_number: form.invoice_number,
        invoice_date: form.invoice_date,
        base_amount: Number(form.base_amount) || 0,
        vat_amount: Number(form.vat_amount) || 0,
        vat_percent: Number(form.vat_percent) || 0,
        total_amount: Number(form.total_amount) || 0,
        main_account: form.main_account,
        expense_kind: form.expense_kind,
        vehicle_id: form.vehicle_id || "",
        notes: form.notes || "",
        section_id: sectionId || "",
        image_data_url: imageDataUrl,
        image_already_scanned: true,
        scan_filter: scanFilter,
        scan_meta: scanQuality ? { quality: scanQuality } : {},
        ocr_confidence: form.ocr_confidence,
        ocr_engine: form.ocr_engine,
      });
      toast.success(`✅ Gasto #${data.correlativo} guardado`);
      onCreated?.(data);
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="expense-new-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[95vh] overflow-hidden flex flex-col">
        <header className="flex items-center justify-between p-4 border-b border-neutral-100 shrink-0">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-orange-500 text-white p-1.5">
              <ScanSearch className="h-5 w-5" />
            </div>
            <div>
              <div className="font-bold text-base">Nuevo gasto</div>
              <div className="text-[11px] text-neutral-500">
                Foto → OCR → PDF → OneDrive · <b>&lt;15s</b>
              </div>
              {sectionLabel && (
                <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-orange-100 text-orange-800 text-[10px] font-bold px-2 py-0.5">
                  <span className="opacity-70">Sección:</span> {sectionLabel}
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-neutral-100"
                  data-testid="expense-new-close">
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="overflow-y-auto p-5 space-y-4 grow">
          {/* PASO 1 · Captura — dos flujos independientes (Foto vs PDF).
              V1.40.4 · Motor aislado, mismo que DNI/Cuestionario. */}
          {step === 1 && (
            <div className="py-2 space-y-4" data-testid="expense-new-step-1">
              {/* Inputs invisibles (uno para foto, otro para PDF) */}
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPhotoInputChange}
                className="hidden"
                data-testid="expense-new-photo-input"
              />
              <input
                ref={pdfInputRef}
                type="file"
                accept="application/pdf"
                onChange={onPdfInputChange}
                className="hidden"
                data-testid="expense-new-pdf-input"
              />

              {/* ─── Bloque FOTO ────────────────────────────── */}
              {!photoPreview ? (
                <button
                  type="button"
                  onClick={() => setGuideOpen(true)}
                  data-testid="expense-new-photo-btn"
                  className="w-full rounded-2xl border-2 border-neutral-200 bg-white hover:border-orange-400 hover:shadow-md transition-all px-5 py-6 flex items-center gap-4 text-left focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <div className="rounded-full p-3 bg-orange-100 text-orange-600 shrink-0">
                    <Camera className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-bold text-neutral-800">Añadir Foto</div>
                    <div className="text-[11px] text-neutral-500 mt-0.5">
                      Cámara del móvil o galería · redimensionado automático a 1600 px
                    </div>
                  </div>
                  <span className="text-orange-600 text-sm font-semibold">→</span>
                </button>
              ) : (
                <div className="rounded-2xl border-2 border-orange-300 bg-orange-50/40 p-4" data-testid="expense-new-photo-preview">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="rounded-full h-6 w-6 bg-orange-100 text-orange-600 flex items-center justify-center">
                      <Camera className="h-3.5 w-3.5" />
                    </div>
                    <div className="text-xs font-bold text-neutral-800">Foto capturada</div>
                    <div className="ml-auto text-[10px] text-neutral-500">
                      Revísala antes de continuar
                    </div>
                  </div>
                  <div className="rounded-lg overflow-hidden border border-neutral-200 bg-white flex items-center justify-center max-h-[360px]">
                    <img
                      src={photoPreview}
                      alt="Vista previa"
                      className="max-w-full max-h-[360px] object-contain"
                      data-testid="expense-new-photo-preview-img"
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      type="button"
                      onClick={retakePhoto}
                      className="btn-secondary !h-9 !px-3 !text-xs"
                      data-testid="expense-new-photo-retake"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Repetir foto
                    </button>
                    {/* V1.77 · Mismo CROP que el área de documentos del alumno */}
                    <button
                      type="button"
                      onClick={() => setCropOpen(true)}
                      className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 text-xs font-semibold"
                      data-testid="expense-new-photo-crop"
                      title="Recortar 4 esquinas, rotar y aplicar filtro antes del OCR"
                    >
                      <ScanSearch className="h-3.5 w-3.5" /> Recortar
                    </button>
                    {cropped && (
                      <span className="text-[10px] font-semibold text-indigo-700" data-testid="expense-new-photo-cropped-badge">
                        Recorte aplicado
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={confirmPhotoAndOcr}
                      className="btn-primary !h-9 !px-4 !text-xs ml-auto"
                      data-testid="expense-new-photo-confirm"
                    >
                      Continuar → OCR <Sparkles className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}

              {/* ─── Bloque PDF (drag&drop + botón) ─────────── */}
              <div
                onDragOver={onPdfDragOver}
                onDragEnter={onPdfDragOver}
                onDragLeave={onPdfDragLeave}
                onDrop={onPdfDrop}
                onClick={openPdfPicker}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") openPdfPicker(); }}
                role="button"
                tabIndex={0}
                data-testid="expense-new-pdf-dropzone"
                className={
                  "cursor-pointer rounded-2xl border-2 border-dashed transition-all duration-150 " +
                  "flex flex-col items-center justify-center text-center px-5 py-8 " +
                  (pdfDragOver
                    ? "border-orange-500 bg-orange-50/70 ring-4 ring-orange-200"
                    : "border-neutral-300 bg-neutral-50 hover:border-orange-300 hover:bg-orange-50/30")
                }
              >
                <div className={"rounded-full p-2.5 mb-2 " + (pdfDragOver ? "bg-orange-500 text-white" : "bg-orange-100 text-orange-600")}>
                  <FileText className="h-5 w-5" />
                </div>
                <div className="text-sm font-bold text-neutral-800">
                  {pdfDragOver ? "Suelta el PDF aquí" : "Añadir PDF"}
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  Arrastra aquí tu PDF o pulsa para seleccionarlo · máx 15 MB
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <>
              {/* Vista previa + filtro */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="rounded-xl overflow-hidden border border-neutral-200 bg-neutral-50 aspect-[3/4] flex items-center justify-center relative">
                    {scannedDataUrl && viewMode === "procesada" ? (
                      <img src={scannedDataUrl} alt="procesada" className="w-full h-full object-contain" />
                    ) : imageDataUrl ? (
                      <img src={imageDataUrl} alt="original" className="w-full h-full object-contain" />
                    ) : (
                      <span className="text-neutral-400 text-sm">Sin imagen</span>
                    )}
                    {ocrBusy && (
                      <div className="absolute inset-0 bg-white/80 flex items-center justify-center backdrop-blur-sm">
                        <OcrProgressStepper step={ocrStep} progress={ocrProgress} route={ocrRoute} />
                      </div>
                    )}
                    {/* V1.43 · Comparador Original / Procesada */}
                    {scannedDataUrl && !ocrBusy && (
                      <div className="absolute top-2 left-2 flex gap-1">
                        <button
                          type="button"
                          onClick={() => setViewMode("original")}
                          data-testid="expense-view-original"
                          className={
                            "text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full shadow-sm " +
                            (viewMode === "original" ? "bg-amber-500 text-white" : "bg-white/90 text-neutral-600 border border-neutral-200")
                          }
                        >
                          Original
                        </button>
                        <button
                          type="button"
                          onClick={() => setViewMode("procesada")}
                          data-testid="expense-view-scanned"
                          className={
                            "text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full shadow-sm " +
                            (viewMode === "procesada" ? "bg-emerald-500 text-white" : "bg-white/90 text-neutral-600 border border-neutral-200")
                          }
                        >
                          Procesada
                        </button>
                      </div>
                    )}
                    {imageDataUrl && !scannedDataUrl && !ocrBusy && (
                      <div className="absolute top-2 left-2 bg-amber-500/90 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-full shadow-sm">
                        Original
                      </div>
                    )}
                  </div>
                  {/* V1.43 · Registro de transformaciones aplicadas */}
                  {scanMeta?.transformations?.length > 0 && (
                    <div className="mt-1.5 text-[10px] text-neutral-500" data-testid="expense-scan-transformations">
                      <b>Tratamiento aplicado:</b> {scanMeta.transformations.join(" · ")}
                    </div>
                  )}
                  {/* Quality badge */}
                  {scanQuality && (
                    <QualityBadge quality={scanQuality} />
                  )}
                  <div className="mt-2">
                    <div className="text-[11px] uppercase tracking-widest font-bold text-neutral-500 mb-1.5">
                      Filtro de escaneado
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {SCAN_FILTERS.map((f) => (
                        <button
                          key={f.key}
                          onClick={() => { setScanFilter(f.key); runOcr(undefined, f.key); }}
                          data-testid={`expense-scan-filter-${f.key}`}
                          className={
                            "rounded-lg border p-2 text-left transition-colors " +
                            (scanFilter === f.key
                              ? "border-orange-500 bg-orange-50"
                              : "border-neutral-200 hover:border-orange-300 bg-white")
                          }
                        >
                          <div className="font-bold text-xs">{f.label}</div>
                          <div className="text-[10px] text-neutral-500">{f.hint}</div>
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={() => runOcr()}
                      disabled={ocrBusy}
                      className="mt-2 text-xs w-full rounded-lg border border-neutral-200 hover:bg-neutral-50 px-2 py-1.5 inline-flex items-center justify-center gap-1"
                      data-testid="expense-ocr-retry"
                    >
                      {ocrBusy
                        ? <><Loader2 className="h-3 w-3 animate-spin" /> Analizando…</>
                        : <><RefreshCw className="h-3 w-3" /> Volver a ejecutar OCR</>}
                    </button>
                    <button
                      onClick={() => runOcr(imageDataUrl, undefined, { skipScan: true })}
                      disabled={ocrBusy}
                      className="mt-1.5 text-xs w-full rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 px-2 py-1.5 inline-flex items-center justify-center gap-1 font-semibold"
                      data-testid="expense-ocr-use-original"
                    >
                      <Camera className="h-3 w-3" /> Usar original para OCR (sin retoques)
                    </button>
                    {form.ocr_confidence > 0 && (
                      <div className="mt-2 text-[10px] text-neutral-500 flex items-center gap-1">
                        <Sparkles className="h-3 w-3 text-emerald-600" />
                        OCR confianza: <b>{(form.ocr_confidence * 100).toFixed(0)}%</b>
                        {form.ocr_engine && <span className="opacity-60">· {form.ocr_engine}</span>}
                      </div>
                    )}
                  </div>
                </div>

                {/* Form */}
                <div className="space-y-2.5">
                  {/* Proveedor */}
                  <Row label="Proveedor conocido">
                    <select
                      value={form.supplier_id}
                      onChange={(e) => onSupplierPick(e.target.value)}
                      className={fieldCls}
                      data-testid="expense-supplier-picker"
                    >
                      <option value="">— Elegir de la lista o escribir abajo —</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}{s.cif ? ` (${s.cif})` : ""}
                        </option>
                      ))}
                    </select>
                  </Row>
                  <Row label="Nombre proveedor">
                    <input value={form.supplier_name}
                           onChange={(e) => setField("supplier_name", e.target.value)}
                           className={fieldCls} data-testid="expense-supplier-name" />
                  </Row>
                  <div className="grid grid-cols-2 gap-2">
                    <Row label="CIF">
                      <input value={form.supplier_cif}
                             onChange={(e) => setField("supplier_cif", e.target.value.toUpperCase())}
                             className={fieldCls} data-testid="expense-supplier-cif" />
                    </Row>
                    <Row label="Nº factura">
                      <input value={form.invoice_number}
                             onChange={(e) => setField("invoice_number", e.target.value)}
                             className={fieldCls} data-testid="expense-invoice-number" />
                    </Row>
                  </div>
                  <Row label="Fecha factura">
                    <input type="date" value={form.invoice_date}
                           onChange={(e) => setField("invoice_date", e.target.value)}
                           className={fieldCls} data-testid="expense-invoice-date" />
                  </Row>
                  <div className="grid grid-cols-3 gap-2">
                    <Row label="Base (€)">
                      <input type="number" step="0.01" value={form.base_amount}
                             onChange={(e) => setField("base_amount", e.target.value)}
                             className={fieldCls} data-testid="expense-base" />
                    </Row>
                    <Row label="IVA (€)">
                      <input type="number" step="0.01" value={form.vat_amount}
                             onChange={(e) => setField("vat_amount", e.target.value)}
                             className={fieldCls} data-testid="expense-vat" />
                    </Row>
                    <Row label="Total (€)">
                      <input type="number" step="0.01" value={form.total_amount}
                             onChange={(e) => setField("total_amount", e.target.value)}
                             className={fieldCls + " font-bold text-orange-700"}
                             data-testid="expense-total" />
                    </Row>
                  </div>
                  {/* Cuenta jerárquica */}
                  <div className="grid grid-cols-2 gap-2">
                    <Row label="Cuenta principal *">
                      <select value={form.main_account}
                              onChange={(e) => { setField("main_account", e.target.value); setField("expense_kind", ""); }}
                              className={fieldCls} data-testid="expense-main-account">
                        <option value="">—</option>
                        {Object.keys(tree).sort().map((m) => (
                          <option key={m} value={m}>{m}</option>
                        ))}
                      </select>
                    </Row>
                    <Row label="Tipo de gasto *">
                      <select value={form.expense_kind}
                              onChange={(e) => setField("expense_kind", e.target.value)}
                              className={fieldCls}
                              disabled={!form.main_account}
                              data-testid="expense-kind">
                        <option value="">—</option>
                        {kindOptions.map((k) => (
                          <option key={k} value={k}>{k}</option>
                        ))}
                      </select>
                    </Row>
                  </div>
                  <Row label="Notas">
                    <input value={form.notes}
                           onChange={(e) => setField("notes", e.target.value)}
                           className={fieldCls} data-testid="expense-notes" />
                  </Row>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-neutral-100 p-4 flex items-center justify-between gap-2 shrink-0">
          <button onClick={onClose} className="btn-secondary !h-10 !px-4">
            Cancelar
          </button>
          {step === 2 && (
            <button
              onClick={save}
              disabled={!canSave || saving}
              data-testid="expense-save-btn"
              className="btn-primary !h-10 !px-5"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Guardar gasto
            </button>
          )}
        </footer>
      </div>
      {/* V1.42 · SmartInvoiceCropDialog eliminado del flujo de cámara.
          El editor manual (ManualCropDialog) del módulo Alumnos/Documentos
          cubre la limpieza posterior por parte de matriz/franquicia. */}
      {/* V1.77 · CROP manual de la foto antes del OCR. Reutiliza el mismo
          diálogo del área de documentos del alumno en modo local. */}
      <ManualCropDialog
        open={cropOpen}
        srcDataUrl={photoPreview}
        filename="foto-gasto.jpg"
        onClose={() => setCropOpen(false)}
        onApply={(dataUrl, cfg) => {
          setPhotoPreview(dataUrl);
          setCropped(!!cfg);
        }}
      />
      {/* V1.41 · Guía visual previa a la captura (encuadre correcto). */}
      <CaptureGuideDialog
        open={guideOpen}
        docType="factura"
        onCancel={() => setGuideOpen(false)}
        onContinue={() => { setGuideOpen(false); openPhotoPicker(); }}
      />
    </div>
  );
}

// V1.39.1 · Stepper visual mientras el OCR está en curso
// V1.40.1 · Si `route === "pdf_native"` OCULTA el paso "prep" — un PDF
// digital no necesita preprocesamiento y va directo a "Extrayendo datos".
function OcrProgressStepper({ step, progress = 0, route = null }) {
  const allItems = [
    { key: "prep",     label: "Preparando imagen",       hint: "Perspectiva, sombras, filtro" },
    { key: "llm",      label: "Extrayendo datos con IA", hint: "Gemini Flash · escaneando..." },
    { key: "supplier", label: "Reconociendo proveedor",  hint: "Buscando en tu base de datos" },
  ];
  const items = route === "pdf_native"
    ? allItems.filter((it) => it.key !== "prep")
    : allItems;
  const activeIdx = Math.max(0, items.findIndex((s) => s.key === step));
  const pct = Math.max(0, Math.min(100, progress));
  return (
    <div
      className="w-full max-w-[300px] px-4 py-4 rounded-xl bg-white shadow-lg border border-neutral-200"
      data-testid="ocr-progress-stepper"
    >
      <div className="flex items-center gap-2 mb-3">
        <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
        <div className="text-sm font-bold text-neutral-800">
          {route === "pdf_native" ? "Analizando PDF…" : "Analizando ticket…"}
        </div>
        <div className="ml-auto text-[11px] font-mono text-orange-600 tabular-nums">{pct}%</div>
      </div>
      {/* Barra de progreso real */}
      <div className="h-1.5 w-full rounded-full bg-neutral-200 overflow-hidden mb-3">
        <div
          className="h-full bg-orange-500 transition-all duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      {route === "pdf_native" && (
        <div className="mb-2 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
          PDF digital · lectura nativa (sin conversión)
        </div>
      )}
      <ol className="space-y-2">
        {items.map((it, i) => {
          const done = i < activeIdx;
          const active = i === activeIdx;
          return (
            <li key={it.key} className="flex items-start gap-2">
              <span
                className={
                  "mt-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-bold shrink-0 " +
                  (done
                    ? "bg-emerald-500 text-white"
                    : active
                      ? "bg-orange-500 text-white animate-pulse"
                      : "bg-neutral-200 text-neutral-500")
                }
              >
                {done ? "✓" : i + 1}
              </span>
              <div className="min-w-0">
                <div className={"text-[12px] font-semibold " + (active ? "text-orange-700" : done ? "text-emerald-700" : "text-neutral-500")}>
                  {it.label}
                </div>
                <div className="text-[10px] text-neutral-500">{it.hint}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

const fieldCls =
  "w-full h-10 rounded-lg border border-neutral-300 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:bg-neutral-50 disabled:text-neutral-400";

function Row({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

const HUMAN_PROBLEMS = {
  blur: "Borroso",
  dark: "Muy oscuro",
  overexposed: "Sobreexpuesto",
  shadowed: "Con sombras",
  edge_cut: "Corte en bordes",
};

function QualityBadge({ quality }) {
  const ok = quality?.ok;
  const problems = quality?.problems || [];
  const m = quality?.metrics || {};
  return (
    <div
      className={
        "mt-2 rounded-lg border p-2 text-[11px] " +
        (ok
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-amber-300 bg-amber-50 text-amber-800")
      }
      data-testid="expense-scan-quality"
    >
      <div className="font-bold flex items-center gap-1">
        {ok ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
        Calidad documental: {ok ? "OK — listo para OneDrive" : "Revisar antes de guardar"}
      </div>
      {problems.length > 0 && (
        <div className="mt-1">
          {problems.map((p) => (
            <span key={p} className="inline-block bg-amber-200 text-amber-900 rounded px-1.5 py-0.5 mr-1 mb-1 text-[10px] font-bold">
              {HUMAN_PROBLEMS[p] || p}
            </span>
          ))}
          <div className="text-[10px] opacity-70 mt-0.5">
            Sugerencia: prueba el filtro <b>Escáner</b> o toma una nueva foto con mejor luz.
          </div>
        </div>
      )}
      <div className="mt-1 text-[10px] opacity-70 tabular-nums">
        nitidez={m.blur} · brillo={m.brightness} · sombras={((m.dark_ratio||0)*100).toFixed(0)}%
      </div>
    </div>
  );
}
